/**
 * lib/audit.ts
 * ------------------------------------------------------------------
 * Centralised audit logging for the LKC Academic Platform.
 *
 * Every significant action writes to `reportAuditLog` with a
 * consistent structure. This enables investigation, accountability,
 * and data integrity verification.
 *
 * Categories:
 *   - auth: login, logout, password changes
 *   - admin: user creation, role changes, settings changes
 *   - academic: marks entry/edit, comment edits, report generation
 *   - attendance: daily attendance saves
 *   - discipline: incident creation/edit/delete
 *   - admissions: student intake, profile edits
 *   - communication: emails sent, WhatsApp messages, announcements
 *   - finance: billing, payments, reconciliation, fee/discount config
 *   - hr: payroll, payslips, leave, deductions, staff/contract changes
 * ------------------------------------------------------------------
 */
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';

export interface AuditEntry {
  /** What happened — verb phrase: 'marks_saved', 'comment_edited', etc */
  action: string;
  /** Broad category for filtering */
  category: 'auth' | 'admin' | 'academic' | 'attendance' | 'discipline' | 'admissions' | 'communication' | 'finance' | 'hr';
  /** Who did it */
  actorId: string;
  actorEmail?: string;
  actorName?: string;
  actorRole?: string;
  /** What was affected */
  targetId?: string;
  targetType?: 'user' | 'student' | 'teacher' | 'class' | 'report' | 'comment' | 'marks' | 'attendance' | 'discipline' | 'achievement' | 'announcement' | 'setting' | 'journal' | 'application';
  targetName?: string;
  /** Additional context — varies by action */
  details?: Record<string, unknown>;
  /** ISO timestamp */
  createdAt: string;
}

/**
 * Write an audit log entry. Fire-and-forget — errors are logged
 * to console but never block the calling action.
 */
export async function logAudit(entry: Omit<AuditEntry, 'createdAt'>): Promise<void> {
  try {
    await addDoc(collection(db, COLLECTIONS.auditLog), {
      ...entry,
      createdAt: new Date().toISOString(),
      // Tamper-evident server-set timestamp (the client createdAt above uses
      // the browser clock and is kept for display/sorting).
      serverTs: serverTimestamp(),
      // Device/browser fingerprint for forensic identification
      device: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenSize: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
        // Standalone = installed PWA vs browser
        displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'pwa' : 'browser',
      },
    });
  } catch (e) {
    console.error('Audit log write failed:', e);
  }
}

/**
 * Convenience: build audit entry from common app context.
 */
export function auditFromContext(
  userData: { uid?: string; email?: string; displayName?: string; role?: string } | null,
  action: string,
  category: AuditEntry['category'],
  opts?: {
    targetId?: string;
    targetType?: AuditEntry['targetType'];
    targetName?: string;
    details?: Record<string, unknown>;
  },
): void {
  logAudit({
    action,
    category,
    actorId: userData?.uid ?? 'unknown',
    actorEmail: userData?.email ?? undefined,
    actorName: userData?.displayName ?? undefined,
    actorRole: userData?.role ?? undefined,
    ...opts,
  });
}

/* ==================================================================
   ROLE PERMISSIONS MODEL — DOCUMENTATION
   ==================================================================

   The system uses a SINGLE ROLE per user. A teacher who is both
   a "Head of Year" and a "Subject Teacher" gets ONE role assigned —
   whichever has higher permissions.

   Role hierarchy (highest to lowest):
     admin > headteacher > editor = hoy = hod > classTeacher > subjectTeacher > admissions > parent

   Recommended approach for dual-responsibility teachers:
   - A HOY who also teaches: assign 'hoy' role. HOY inherits
     canViewAllMarks and canViewProgress, so they can see marks
     and progress for all students, plus enter HOY comments.
     Their TEACHING assignments (classAssignments on the Teacher
     record) determine which marks they can ENTER — this is
     enforced by canEnterMarksFor() regardless of role.
   - An HOD who also teaches: assign 'hod' role. Same logic.
   - A class teacher who is also HOY: assign 'hoy' (higher).
     Class-teacher-level permissions (entering class comments,
     attendance) work because they check the Teacher record's
     classTeacherOf field, not the user role.

   The linkedTeacherId field on AppUser connects the login account
   to the Teacher record, enabling the system to look up:
   - Which classes this user teaches
   - Which subjects in which classes
   - Whether they're a class teacher
   These are data-level checks, not role-level. A user with any
   teaching role (subjectTeacher through headteacher) gets their
   marks-entry scope from their Teacher record.

   ================================================================== */
